/** UploadDocumentModal — 数据库与个人空间的「上传」弹窗（跨事项视图）。
 *
 * 目标事项可选：选了事项，文档归属该事项；不选，文档落入调用方的个人空间
 * （后端 `owner_scope=personal`，不要求事项），律师不必先建客户和事项就能把
 * 一份合同放进系统并发起审查，事后可经「转移归属」挂到事项。调用方可用
 * `defaultMatterId` 预填事项。
 *
 * 「目标事项」下拉内置「新建事项」入口（AF4）：想直接归到事项又还没有事项的
 * 律师不必离开当前页面。
 */

import { useState, useCallback, useMemo } from "react";
import { Modal, Input, Select, Upload, Progress, message } from "antd";
import {
  InboxOutlined,
  FileTextOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useUploadDocument, useMatters } from "../../api/queries";
import {
  formatFileSize,
  buildUploadNotices,
  settleUploadOutcomes,
} from "../../utils";
import type { FileUploadOutcome } from "../../utils";
import type { Matter } from "../../types";
import SelectCreateFooter from "../common/SelectCreateFooter";
import MatterFormModal from "../matter/MatterFormModal";
import styles from "../../styles/client.module.css";
import kStyles from "../../styles/knowledge.module.css";
import uploadStyles from "../../styles/upload-modal.module.css";
import icon from "../../styles/icon.module.css";

const { Dragger } = Upload;

const ACCEPTED_EXTENSIONS =
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.ofd,.rar,.zip";
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

interface FileUploadState {
  uid: string;
  file: File;
  name: string;
  size: number;
  status: "pending" | "uploading" | "success" | "error";
  percent: number;
  errorMsg?: string;
}

interface UploadDocumentModalProps {
  open: boolean;
  onClose: () => void;
  /** 预填的目标事项；为空则默认上传到个人空间。 */
  defaultMatterId?: string | null;
}

export default function UploadDocumentModal({
  open,
  onClose,
  defaultMatterId = null,
}: UploadDocumentModalProps) {
  const [matterId, setMatterId] = useState<string | null>(defaultMatterId);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("contract");
  const [fileStates, setFileStates] = useState<FileUploadState[]>([]);
  const [uploading, setUploading] = useState(false);
  const [matterModalOpen, setMatterModalOpen] = useState(false);
  // 刚建好的事项先并进选项，不等列表重新拉取——否则这段时间下拉里没有该项，
  // Select 会把选中值原样显示成一串 id。
  const [justCreatedMatter, setJustCreatedMatter] = useState<Matter | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const uploadDoc = useUploadDocument();
  const { data: mattersData, isLoading: mattersLoading } = useMatters({
    pageSize: 100,
  });

  const matterOptions = useMemo(() => {
    const items = mattersData?.data ?? [];
    const merged =
      justCreatedMatter && !items.some((m) => m.id === justCreatedMatter.id)
        ? [justCreatedMatter, ...items]
        : items;
    return merged.map((m) => ({
      value: m.id,
      label: m.name,
    }));
  }, [mattersData, justCreatedMatter]);

  const handleBeforeUpload = useCallback(
    (file: File, fileList: File[]) => {
      const validFiles = fileList.filter((f) => {
        if (f.size > MAX_FILE_SIZE) {
          messageApi.error(`${f.name} 超过 ${MAX_FILE_SIZE_MB}MB 限制`);
          return false;
        }
        return true;
      });
      if (validFiles.length === 0) return false;

      const newStates: FileUploadState[] = validFiles.map((f) => ({
        uid: `${f.name}-${f.size}-${Date.now()}`,
        file: f,
        name: f.name,
        size: f.size,
        status: "pending" as const,
        percent: 0,
      }));
      setFileStates((prev) => {
        const existing = prev.filter((s) => s.status !== "pending");
        return [...existing, ...newStates];
      });
      return false;
    },
    [messageApi],
  );

  const handleRemoveFile = useCallback((uid: string) => {
    setFileStates((prev) => prev.filter((s) => s.uid !== uid));
  }, []);

  const canSubmit =
    fileStates.some((s) => s.status === "pending") &&
    !uploading;

  const handleUpload = useCallback(async () => {
    const pendingFiles = fileStates.filter((s) => s.status === "pending");
    if (pendingFiles.length === 0) {
      messageApi.warning("请选择文件");
      return;
    }

    setUploading(true);
    const results = await Promise.allSettled(
      pendingFiles.map(async (fs): Promise<FileUploadOutcome> => {
        setFileStates((prev) =>
          prev.map((s) =>
            s.uid === fs.uid ? { ...s, status: "uploading", percent: 30 } : s,
          ),
        );
        try {
          const res = await uploadDoc.mutateAsync({
            matterId,
            title: title.trim() || fs.name,
            type: docType,
            file: fs.file,
          });
          setFileStates((prev) =>
            prev.map((s) =>
              s.uid === fs.uid ? { ...s, status: "success", percent: 100 } : s,
            ),
          );
          return { saved: true, ragIndexStatus: res.data.ragIndexStatus };
        } catch (err) {
          const errorMsg =
            err instanceof Error ? err.message : "上传失败，请检查文件格式和网络";
          setFileStates((prev) =>
            prev.map((s) =>
              s.uid === fs.uid
                ? { ...s, status: "error", percent: 0, errorMsg }
                : s,
            ),
          );
          return { saved: false };
        }
      }),
    );
    setUploading(false);

    const notices = buildUploadNotices(settleUploadOutcomes(results));
    if (notices.success) messageApi.success(notices.success);
    if (notices.error) messageApi.error(notices.error);
    if (notices.indexWarning) messageApi.warning(notices.indexWarning);
    if (notices.failedCount === 0) {
      setTimeout(() => {
        handleClose();
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileStates, matterId, title, docType, uploadDoc, messageApi]);

  const handleClose = useCallback(() => {
    if (uploading) return;
    setMatterId(defaultMatterId);
    setTitle("");
    setDocType("contract");
    setFileStates([]);
    onClose();
  }, [uploading, onClose, defaultMatterId]);

  const hasFiles = fileStates.length > 0;
  const allSuccess = hasFiles && fileStates.every((s) => s.status === "success");
  const hasErrors = fileStates.some((s) => s.status === "error");

  const renderStatusIcon = (status: FileUploadState["status"]) => {
    switch (status) {
      case "success":
        return <CheckCircleOutlined className={uploadStyles.fileItemStatusSuccess} />;
      case "error":
        return <CloseCircleOutlined className={uploadStyles.fileItemStatusError} />;
      case "uploading":
        return <LoadingOutlined className={uploadStyles.fileItemStatusUploading} />;
      default:
        return null;
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="上传文档"
        open={open}
        onOk={allSuccess ? handleClose : handleUpload}
        onCancel={handleClose}
        confirmLoading={uploading}
        okText={allSuccess ? "完成" : "上传"}
        cancelText="取消"
        destroyOnHidden
        width={560}
        rootClassName="jerryUploadDocModal"
        okButtonProps={{
          disabled: !canSubmit && !allSuccess,
        }}
      >
        <div className={kStyles.modalBody}>
          {/* 目标事项（可选；不选则上传到个人空间） */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>目标事项</label>
            <Select
              value={matterId ?? undefined}
              onChange={(v) => setMatterId(v ?? null)}
              options={matterOptions}
              loading={mattersLoading}
              placeholder="不选则上传到个人空间，可稍后挂到事项"
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              popupRender={(menu) => (
                <SelectCreateFooter
                  menu={menu}
                  label="新建事项"
                  onCreate={() => setMatterModalOpen(true)}
                  testId="upload-modal-create-matter"
                />
              )}
              notFoundContent={
                mattersLoading ? "加载中..." : "暂无事项，可在下方新建"
              }
              data-testid="upload-modal-matter-select"
            />
          </div>

          {/* 文档标题 */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>文档标题</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="不填则使用文件名"
            />
          </div>

          {/* 文档类型 */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>文档类型</label>
            <Select
              value={docType}
              onChange={(v) => setDocType(v)}
              options={[
                { value: "contract", label: "合同" },
                { value: "correspondence", label: "函件" },
                { value: "memo", label: "备忘录" },
                { value: "meeting_minutes", label: "会议纪要" },
                { value: "due_diligence_report", label: "尽调报告" },
                { value: "other", label: "其他" },
              ]}
            />
          </div>

          {/* 拖拽区 / 文件列表 */}
          {!hasFiles && (
            <Dragger
              multiple
              showUploadList={false}
              beforeUpload={handleBeforeUpload}
              accept={ACCEPTED_EXTENSIONS}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined
                  style={{ fontSize: 40, color: "var(--jerry-text-tertiary)" }}
                />
              </p>
              <p className={kStyles.dropText}>
                拖拽文件到此处，或{" "}
                <span className={kStyles.browseLink}>浏览文件</span>
              </p>
              <p className={kStyles.dropHint}>
                支持 PDF、Word、Excel、PPT、TXT、图片、OFD、压缩包，单个文件不超过{" "}
                {MAX_FILE_SIZE_MB}MB
              </p>
            </Dragger>
          )}

          {hasFiles && (
            <div className={uploadStyles.fileListSection}>
              {fileStates.map((fs) => (
                <div key={fs.uid} className={uploadStyles.fileItemRow}>
                  <span className={uploadStyles.fileItemIcon}>
                    <FileTextOutlined />
                  </span>
                  <div className={uploadStyles.fileItemInfo}>
                    <div className={uploadStyles.fileItemName}>{fs.name}</div>
                    <div className={uploadStyles.fileItemMeta}>
                      <span>{formatFileSize(fs.size)}</span>
                    </div>
                    {fs.status === "uploading" && (
                      <Progress
                        percent={fs.percent}
                        size="small"
                        className={uploadStyles.fileItemProgress}
                        showInfo={false}
                      />
                    )}
                  </div>
                  <span className={uploadStyles.fileItemStatus}>
                    {renderStatusIcon(fs.status)}
                    {fs.status === "error" && (
                      <span className={uploadStyles.fileItemStatusError}>
                        {fs.errorMsg}
                      </span>
                    )}
                  </span>
                  {fs.status === "pending" && (
                    <button
                      type="button"
                      className={uploadStyles.fileItemRemove}
                      onClick={() => handleRemoveFile(fs.uid)}
                      aria-label="移除文件"
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </div>
              ))}
              {!allSuccess && (
                <Upload
                  multiple
                  showUploadList={false}
                  beforeUpload={handleBeforeUpload}
                  accept={ACCEPTED_EXTENSIONS}
                >
                  <button
                    type="button"
                    className={uploadStyles.addMoreBtn}
                  >
                    添加更多文件
                  </button>
                </Upload>
              )}
            </div>
          )}
        </div>
      </Modal>

      <MatterFormModal
        open={matterModalOpen}
        onClose={() => setMatterModalOpen(false)}
        onCreated={(created) => {
          setJustCreatedMatter(created);
          setMatterId(created.id);
        }}
      />
    </>
  );
}
